import React, {ReactNode, useCallback, useState} from 'react';
import cn from 'classnames';

import Check from 'shared/ui/icons/Check';
import Cancel from 'shared/ui/icons/Cancel';
import {ITariff} from 'entities/tariff';
import {IUserOutput} from 'entities/user';
import {isTranslate} from 'shared/lib/lang';
import {formatLimit, formatTariffLimit} from 'features/tariff/lib/formatLimit';

import {plural, t} from '@lingui/core/macro';

import TariffModal from '../TariffModal';
import TariffLabel from './TariffLabel/TariffLabel';
import TariffHeader from './TariffHeader/TariffHeader';
import TariffPrice from './TariffPrice/TariffPrice';
import TariffLimitGroup from './TariffLimitGroup/TariffLimitGroup';
import TariffLimitRow from './TariffLimitRow/TariffLimitRow';

import styles from './Tariff.module.scss';

interface ISingleTariffProps {
    item: ITariff,
    user: IUserOutput | null,
}

interface ILimitRow {
    label: string,
    value: ReactNode,
}

interface ILimitGroup {
    title: string,
    rows: ILimitRow[],
    collapsible?: boolean,
}

const Tariff: React.FC<ISingleTariffProps> = ({item, user}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const isActive = item.id === user?.tariff.id;

    const checkLimits = [
        {label: t`Мониторинг SSL и домена`, limit: 'monitor_ssl_enabled'},
        {label: t`Проверка редиректа HTTPS`, limit: 'monitor_ssl_redirect_watch_enabled'},
        {label: t`Проверка robots.txt`, limit: 'monitor_robots_txt_watch_enabled'},
        {label: t`Расширенный мониторинг`, limit: 'monitor_extended_methods'},
        {label: t`Учащенный мониторинг`, limit: 'monitor_frequent_checking'},
        {label: t`Скриншот страницы ошибки`, limit: 'monitor_screenshot_on_error'},
        {label: t`HTTP Basic Auth`, limit: 'monitor_http_basic_auth_enabled'},
        {label: t`Пользовательские заголовки запроса`, limit: 'monitor_custom_user_agent_enabled'},
    ] as const;
    const visibleCheckLimits = checkLimits.filter(({limit}) => (
        limit !== 'monitor_custom_user_agent_enabled' || !isTranslate()
    ));

    const limitGroups: ILimitGroup[] = [
        {
            title: t`Мониторинг`,
            rows: [
                {
                    label: t`Мониторы сайта`,
                    value: formatTariffLimit(item.monitor_max_count_limit),
                },
                {
                    label: t`Мониторы домена`,
                    value: formatTariffLimit(item.domain_monitor_max_count_limit),
                },
                {
                    label: t`Мониторы сервера`,
                    value: formatTariffLimit(item.server_max_count_limit),
                },
                {
                    label: t`Мониторов портов`,
                    value: formatTariffLimit(item.port_monitor_max_count_limit),
                },
                {
                    label: t`Гео локации`,
                    value: formatTariffLimit(item.location_max_count_limit),
                },
                {
                    label: t`Интервал мониторинга`,
                    value: `${formatLimit(item.monitor_min_check_interval_limit)} ${t`мин`}`,
                },
                {
                    label: t`Правила уведомлений (серверы)`,
                    value: formatTariffLimit(item.server_alert_rules_max_count_limit),
                },
            ],
        },
        {
            title: t`Отчётность`,
            collapsible: true,
            rows: [
                {
                    label: t`Страницы статуса`,
                    value: formatTariffLimit(item.status_pages_max_count_limit),
                },
                {
                    label: t`Отчёты`,
                    value: formatTariffLimit(item.reports_max_count_limit),
                },
            ],
        },
        {
            title: t`Проверки`,
            collapsible: true,
            rows: visibleCheckLimits.map(({label, limit}) => ({
                label,
                value: item[limit] ? <Check /> : <Cancel />,
            })),
        },
    ];

    const periodLabel = item.period === 0
        ? t`Бессрочно`
        : plural(item.period, {one: '# день', few: '# дня', other: '# дней'});

    const onPayClick = useCallback(() => setIsModalOpen(true), []);

    const getVariantClassName = () => {
        if (item.is_archive) {
            return styles.archive;
        }

        if (isActive) {
            return styles.active;
        }

        if (item.is_recommended) {
            return styles.recommend;
        }

        return styles.default;
    };

    return (
        <>
            <div className={cn(
                styles.container,
                getVariantClassName(),
                {[styles.en]: isTranslate()},
            )}>
                <TariffLabel
                    isActive={isActive}
                    tariff={item}
                    days={user?.tariff_days_left_human_readable}
                />

                <div className={styles.body}>
                    <TariffHeader name={item.name} period={periodLabel} />

                    {limitGroups.map(({title, rows, collapsible}) => (
                        <TariffLimitGroup
                            key={title}
                            title={title}
                            className={cn({[styles.collapsed]: collapsible && !isExpanded})}
                        >
                            {rows.map(({label, value}) => (
                                <TariffLimitRow
                                    key={label}
                                    label={label}
                                    value={value}
                                />
                            ))}
                        </TariffLimitGroup>
                    ))}

                    <TariffPrice
                        price={item.price}
                        isActive={isActive}
                        isArchive={item.is_archive}
                        onPayClick={onPayClick}
                        onExpandClick={() => setIsExpanded(!isExpanded)}
                        isExpanded={isExpanded}
                    />
                </div>
            </div>

            <TariffModal
                open={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
                item={item}
                userCurrentTariff={user?.tariff}
            />
        </>
    );
};

export default Tariff;
